import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// GitHub's MCP server lists these at /mcp/x/all (all toolsets) on 2026-10-22.
// Its tools/list needs a token, which P0 does not hold when it syncs a gateway.
// Get the list with ../scripts/snapshot-mcp-tools.sh, which also removes the
// tools that a token for one repository cannot call.
export const githubAiMcpTools: McpTool[] = [
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title:
        "Get details of GitHub Actions resources (workflows, workflow runs, jobs, and artifacts)",
    },
    description:
      "Get details about specific GitHub Actions resources.\nUse this tool to get details about individual workflows, workflow runs, jobs, and artifacts by their unique IDs.\n",
    inputSchema: {
      properties: {
        method: {
          description: "The method to execute",
          enum: [
            "get_workflow",
            "get_workflow_run",
            "get_workflow_job",
            "download_workflow_run_artifact",
            "get_workflow_run_usage",
            "get_workflow_run_logs_url",
          ],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        resource_id: {
          description:
            "The unique identifier of the resource. This will vary based on the \"method\" provided, so ensure you provide the correct ID:\n- Provide a workflow ID or workflow file name (e.g. ci.yaml) for 'get_workflow' method.\n- Provide a workflow run ID for 'get_workflow_run', 'get_workflow_run_usage', and 'get_workflow_run_logs_url' methods.\n- Provide an artifact ID for 'download_workflow_run_artifact' method.\n- Provide a job ID for 'get_workflow_job' method.\n",
          type: "string",
        },
      },
      required: ["method", "owner", "repo", "resource_id"],
      type: "object",
    },
    name: "actions_get",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List GitHub Actions workflows in a repository",
    },
    description:
      "Tools for listing GitHub Actions resources.\nUse this tool to list workflows in a repository, or list workflow runs, jobs, and artifacts for a specific workflow or workflow run.\n",
    inputSchema: {
      properties: {
        method: {
          description: "The action to perform",
          enum: [
            "list_workflows",
            "list_workflow_runs",
            "list_workflow_jobs",
            "list_workflow_run_artifacts",
          ],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (default: 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description:
            "Results per page for pagination (default: 30, max: 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        resource_id: {
          description:
            "The unique identifier of the resource. This will vary based on the \"method\" provided, so ensure you provide the correct ID:\n- Do not provide any resource ID for 'list_workflows' method.\n- Provide a workflow ID or workflow file name (e.g. ci.yaml) for 'list_workflow_runs' method, or omit to list all workflow runs in the repository.\n- Provide a workflow run ID for 'list_workflow_jobs' and 'list_workflow_run_artifacts' methods.\n",
          type: "string",
        },
        workflow_jobs_filter: {
          description:
            "Filters for workflow jobs. **ONLY** used when method is 'list_workflow_jobs'",
          properties: {
            filter: {
              description: "Filters jobs by their completed_at timestamp",
              enum: ["latest", "all"],
              type: "string",
            },
          },
          type: "object",
        },
        workflow_runs_filter: {
          description:
            "Filters for workflow runs. **ONLY** used when method is 'list_workflow_runs'",
          properties: {
            actor: {
              description: "Filter to a specific GitHub user's workflow runs.",
              type: "string",
            },
            branch: {
              description:
                "Filter workflow runs to a specific Git branch. Use the name of the branch.",
              type: "string",
            },
            event: {
              description: "Filter workflow runs to a specific event type",
              enum: [
                "branch_protection_rule",
                "check_run",
                "check_suite",
                "create",
                "delete",
                "deployment",
                "deployment_status",
                "discussion",
                "discussion_comment",
                "fork",
                "gollum",
                "issue_comment",
                "issues",
                "label",
                "merge_group",
                "milestone",
                "page_build",
                "public",
                "pull_request",
                "pull_request_review",
                "pull_request_review_comment",
                "pull_request_target",
                "push",
                "registry_package",
                "release",
                "repository_dispatch",
                "schedule",
                "status",
                "watch",
                "workflow_call",
                "workflow_dispatch",
                "workflow_run",
              ],
              type: "string",
            },
            status: {
              description:
                "Filter workflow runs to only runs with a specific status",
              enum: [
                "queued",
                "in_progress",
                "completed",
                "requested",
                "waiting",
              ],
              type: "string",
            },
          },
          type: "object",
        },
      },
      required: ["method", "owner", "repo"],
      type: "object",
    },
    name: "actions_list",
  },
  {
    annotations: {
      destructiveHint: true,
      idempotentHint: false,
      readOnlyHint: false,
      title: "Trigger GitHub Actions workflow actions",
    },
    description:
      "Trigger GitHub Actions workflow operations, including running, re-running, cancelling workflow runs, and deleting workflow run logs.",
    inputSchema: {
      properties: {
        inputs: {
          description:
            "Inputs the workflow accepts. Only used for 'run_workflow' method.",
          properties: {},
          type: "object",
        },
        method: {
          description: "The method to execute",
          enum: [
            "run_workflow",
            "rerun_workflow_run",
            "rerun_failed_jobs",
            "cancel_workflow_run",
            "delete_workflow_run_logs",
          ],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        ref: {
          description:
            "The git reference for the workflow. The reference can be a branch or tag name. Required for 'run_workflow' method.",
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        run_id: {
          description:
            "The ID of the workflow run. Required for all methods except 'run_workflow'.",
          type: "number",
        },
        workflow_id: {
          description:
            "The workflow ID (numeric) or workflow file name (e.g., main.yml, ci.yaml). Required for 'run_workflow' method.",
          type: "string",
        },
      },
      required: ["method", "owner", "repo"],
      type: "object",
    },
    name: "actions_run_trigger",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title:
        "Add review comment to the requester's latest pending pull request review",
    },
    description:
      "Add review comment to the requester's latest pending pull request review. A pending review needs to already exist to call this (check with the user if not sure).",
    inputSchema: {
      properties: {
        body: {
          description: "The text of the review comment",
          type: "string",
        },
        line: {
          description:
            "The line of the blob in the pull request diff that the comment applies to. For multi-line comments, the last line of the range",
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        path: {
          description:
            "The relative path to the file that necessitates a comment",
          type: "string",
        },
        pullNumber: {
          description: "Pull request number",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        side: {
          description:
            "The side of the diff to comment on. LEFT indicates the previous state, RIGHT indicates the new state",
          enum: ["LEFT", "RIGHT"],
          type: "string",
        },
        startLine: {
          description:
            "For multi-line comments, the first line of the range that the comment applies to",
          type: "number",
        },
        startSide: {
          description:
            "For multi-line comments, the starting side of the diff that the comment applies to. LEFT indicates the previous state, RIGHT indicates the new state",
          enum: ["LEFT", "RIGHT"],
          type: "string",
        },
        subjectType: {
          description: "The level at which the comment is targeted",
          enum: ["FILE", "LINE"],
          type: "string",
        },
      },
      required: ["owner", "repo", "pullNumber", "path", "body", "subjectType"],
      type: "object",
    },
    name: "add_comment_to_pending_review",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Add comment to issue or pull request",
    },
    description:
      "Add a comment and/or reaction to a specific issue or issue comment in a GitHub repository. Use this tool with pull requests as well (in this case pass pull request number as issue_number), but only if user is not asking specifically to add or react to review comments. At least one of body or reaction is required.",
    inputSchema: {
      properties: {
        body: {
          description: "Comment content. Required unless reaction is provided.",
          minLength: 1,
          type: "string",
        },
        comment_id: {
          description:
            "The numeric ID of the issue or pull request comment to react to. Use this for reactions to comments; omit it to react to the issue or pull request itself. Cannot be combined with body.",
          minimum: 1,
          type: "integer",
        },
        issue_number: {
          description:
            "Issue or pull request number to comment on or react to.",
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        reaction: {
          description:
            "Emoji reaction to add. Required unless body is provided.",
          enum: [
            "+1",
            "-1",
            "laugh",
            "confused",
            "heart",
            "hooray",
            "rocket",
            "eyes",
          ],
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "issue_number"],
      type: "object",
    },
    name: "add_issue_comment",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Add reply to pull request comment",
    },
    description:
      "Add a reply and/or reaction to an existing pull request comment. This can create a new comment linked as a reply to the specified comment, add an emoji reaction to the specified comment, or do both. At least one of body or reaction is required.",
    inputSchema: {
      properties: {
        body: {
          description:
            "The text of the reply. Required unless reaction is provided.",
          type: "string",
        },
        commentId: {
          description:
            "The numeric ID of the pull request review comment to reply or react to. Use the number from a #discussion_r... anchor, not the GraphQL thread node ID (PRRT_...).",
          minimum: 1,
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        pullNumber: {
          description: "Pull request number. Required when body is provided.",
          type: "number",
        },
        reaction: {
          description:
            "Emoji reaction to add. Required unless body is provided.",
          enum: [
            "+1",
            "-1",
            "laugh",
            "confused",
            "heart",
            "hooray",
            "rocket",
            "eyes",
          ],
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "commentId"],
      type: "object",
    },
    name: "add_reply_to_pull_request_comment",
  },
  {
    annotations: {
      idempotentHint: false,
      openWorldHint: true,
      readOnlyHint: true,
      title: "Check Dependency Vulnerabilities",
    },
    description:
      "Check a list of dependencies for known vulnerabilities using the GitHub Security Advisory Database.\nThis tool queries the GitHub Advisory Database to find known security vulnerabilities affecting the specified packages.\n\nUse this tool when:\n- You want to check if project dependencies have known vulnerabilities\n- You are adding a new dependency to a project and should check it is safe before adding to the project\n\nThe tool accepts owner, repo, and a list of dependencies with their ecosystems and versions, then returns any matching security advisories with upgrade recommendations where available.",
    inputSchema: {
      properties: {
        dependencies: {
          description: "List of dependencies to check for vulnerabilities",
          items: {
            properties: {
              ecosystem: {
                description:
                  "Package ecosystem (case-insensitive; GitHub Actions also accepts github-actions)",
                enum: [
                  "npm",
                  "pip",
                  "maven",
                  "nuget",
                  "composer",
                  "pub",
                  "actions",
                  "github-actions",
                  "bundler",
                  "gomod",
                  "cargo",
                  "hex",
                ],
                type: "string",
              },
              name: {
                description: "Package name",
                type: "string",
              },
              version: {
                description: "Package version",
                type: "string",
              },
            },
            required: ["name", "version", "ecosystem"],
            type: "object",
          },
          type: "array",
        },
        owner: {
          description: "Repository owner (username or organization)",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "dependencies"],
      type: "object",
    },
    name: "check_dependency_vulnerabilities",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Create branch",
    },
    description: "Create a new branch in a GitHub repository",
    inputSchema: {
      properties: {
        branch: {
          description: "Name for new branch",
          type: "string",
        },
        from_branch: {
          description: "Source branch (defaults to repo default)",
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "branch"],
      type: "object",
    },
    name: "create_branch",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Create or update file",
    },
    description:
      "Create or update a single file in a GitHub repository. \nIf updating, you should provide the SHA of the file you want to update. Use this tool to create or update a file in a GitHub repository remotely; do not use it for local file operations.\n\nTo obtain the current blob SHA before updating, call the get_file_contents tool with the same owner, repo, and path, and set its ref parameter to this tool's branch value. The first text result reports the blob SHA for the requested path.\n\nSHA MUST be provided for existing file updates.\n",
    inputSchema: {
      properties: {
        allow_symlink_write: {
          default: false,
          description:
            "Set true to update a symbolic link itself; content must be its new target path.",
          type: "boolean",
        },
        branch: {
          description: "Branch to create/update the file in",
          type: "string",
        },
        content: {
          description:
            "Content of the file, exactly as it should appear once written. Do not base64-encode it; this server does that before calling the REST API.",
          type: "string",
        },
        message: {
          description: "Commit message",
          type: "string",
        },
        owner: {
          description: "Repository owner (username or organization)",
          type: "string",
          "x-mcp-header": "owner",
        },
        path: {
          description: "Path where to create/update the file",
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        sha: {
          description:
            "The blob SHA of the file being replaced. Required if the file already exists. Retrieve it with get_file_contents using the same owner, repo, and path, with ref set to this tool's branch value.",
          type: "string",
        },
      },
      required: ["owner", "repo", "path", "content", "message", "branch"],
      type: "object",
    },
    name: "create_or_update_file",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Open new pull request",
    },
    description: "Create a new pull request in a GitHub repository.",
    inputSchema: {
      properties: {
        base: {
          description: "Branch to merge into",
          type: "string",
        },
        body: {
          description: "PR description",
          type: "string",
        },
        draft: {
          description: "Create as draft PR",
          type: "boolean",
        },
        head: {
          description: "Branch containing changes",
          type: "string",
        },
        maintainer_can_modify: {
          description: "Allow maintainer edits",
          type: "boolean",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        reviewers: {
          description:
            "GitHub usernames or ORG/team-slug team reviewers to request reviews from",
          items: {
            type: "string",
          },
          type: "array",
        },
        title: {
          description: "PR title",
          type: "string",
        },
      },
      required: ["owner", "repo", "title", "head", "base"],
      type: "object",
    },
    name: "create_pull_request",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Create repository ruleset",
    },
    description:
      "Create a new ruleset at the repository, organization, or enterprise level",
    inputSchema: {
      additionalProperties: false,
      properties: {
        bypass_actors: {
          description: "The actors that can bypass the rules in this ruleset",
          items: {
            additionalProperties: false,
            properties: {
              actor_id: {
                description: "The ID of the actor that can bypass a ruleset",
                type: "number",
              },
              actor_type: {
                description:
                  "The type of actor that can bypass a ruleset. 'EnterpriseOwner' and 'EnterpriseRole' are only valid for 'enterprise' level rulesets.",
                enum: [
                  "Integration",
                  "OrganizationAdmin",
                  "RepositoryRole",
                  "Team",
                  "DeployKey",
                  "User",
                  "EnterpriseOwner",
                  "EnterpriseRole",
                ],
                type: "string",
              },
              bypass_mode: {
                description:
                  "When the specified actor can bypass the ruleset. 'pull_request' only applies to branch rulesets and is not valid for the 'DeployKey' actor type. 'exempt' means rules are not run for that actor and no bypass audit entry is created.",
                enum: ["always", "pull_request", "exempt"],
                type: "string",
              },
            },
            required: ["actor_type", "bypass_mode"],
            type: "object",
          },
          type: "array",
        },
        conditions: {
          description:
            'Conditions for when this ruleset applies, e.g. {"ref_name": {"include": ["refs/heads/main"], "exclude": []}}',
          type: "object",
        },
        enforcement: {
          description:
            "The enforcement level of the ruleset. 'evaluate' allows admins to test rules before enforcing them",
          enum: ["disabled", "active", "evaluate"],
          type: "string",
        },
        enterprise: {
          description: "Enterprise slug. Required when level is 'enterprise'.",
          type: "string",
        },
        level: {
          description:
            "The level at which the ruleset is configured:\n- 'repository': A ruleset on a single repository (requires 'owner' and 'repo').\n- 'organization': A ruleset covering repositories in an organization (requires 'org').\n- 'enterprise': A ruleset covering repositories across an enterprise (requires 'enterprise').",
          enum: ["repository", "organization", "enterprise"],
          type: "string",
        },
        name: {
          description: "The name of the ruleset",
          type: "string",
        },
        org: {
          description:
            "Organization name. Required when level is 'organization'.",
          type: "string",
        },
        owner: {
          description: "Repository owner. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "repo",
        },
        rules: {
          description:
            "An array of rules within the ruleset. Each rule is an object with a 'type' (e.g. 'creation', 'deletion', 'non_fast_forward', 'required_signatures', 'pull_request', 'required_status_checks') and, for rules that need configuration, a 'parameters' object",
          items: {
            additionalProperties: false,
            properties: {
              parameters: {
                description:
                  "Parameters for rule types that require additional configuration",
                type: "object",
              },
              type: {
                description:
                  "The type of rule, e.g. 'creation', 'deletion', 'non_fast_forward', 'required_signatures', 'pull_request', 'required_status_checks'",
                type: "string",
              },
            },
            required: ["type"],
            type: "object",
          },
          type: "array",
        },
        target: {
          description:
            "The target of the ruleset. Defaults to 'branch'. 'repository' is only valid for 'organization' and 'enterprise' level rulesets.",
          enum: ["branch", "tag", "push", "repository"],
          type: "string",
        },
      },
      required: ["level", "name", "enforcement", "rules"],
      type: "object",
    },
    name: "create_repository_ruleset",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Read custom properties",
    },
    description:
      "Read custom properties at the repository, organization, or enterprise level. At the repository level this returns the property values assigned to a repository; at the organization and enterprise levels it returns the property definitions (schema). Select the level with the 'level' parameter.",
    inputSchema: {
      properties: {
        enterprise: {
          description: "Enterprise slug. Required when level is 'enterprise'.",
          type: "string",
        },
        level: {
          description:
            "The level at which custom properties are managed:\n- 'repository': The custom property VALUES assigned to a repository (requires 'owner' and 'repo').\n- 'organization': The custom property DEFINITIONS (schema) for an organization (requires 'org').\n- 'enterprise': The custom property DEFINITIONS (schema) for an enterprise (requires 'enterprise').",
          enum: ["repository", "organization", "enterprise"],
          type: "string",
        },
        org: {
          description:
            "Organization name. Required when level is 'organization'.",
          type: "string",
        },
        owner: {
          description: "Repository owner. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["level"],
      type: "object",
    },
    name: "custom_properties_read",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Set custom properties",
    },
    description:
      "Create or update custom properties at the repository, organization, or enterprise level. At the repository level this sets the property values on a repository (the properties must already be defined for the organization). Organization and enterprise definition writes preserve omitted writable fields by reading current definitions immediately before updating; concurrent definition updates remain last-write-wins. Select the level with the 'level' parameter.",
    inputSchema: {
      properties: {
        enterprise: {
          description: "Enterprise slug. Required when level is 'enterprise'.",
          type: "string",
        },
        level: {
          description:
            "The level at which custom properties are managed:\n- 'repository': The custom property VALUES assigned to a repository (requires 'owner' and 'repo').\n- 'organization': The custom property DEFINITIONS (schema) for an organization (requires 'org').\n- 'enterprise': The custom property DEFINITIONS (schema) for an enterprise (requires 'enterprise').",
          enum: ["repository", "organization", "enterprise"],
          type: "string",
        },
        org: {
          description:
            "Organization name. Required when level is 'organization'.",
          type: "string",
        },
        owner: {
          description: "Repository owner. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "owner",
        },
        properties: {
          description:
            "The custom properties to create or update. At the repository level each item assigns a value ('property_name' and 'value'); at the organization and enterprise levels each item defines the schema ('property_name' and 'value_type', plus optional definition fields).",
          items: {
            oneOf: [
              {
                additionalProperties: false,
                description: "A repository-level custom property value.",
                properties: {
                  property_name: {
                    description: "The name of the custom property.",
                    type: "string",
                  },
                  value: {
                    description:
                      "Repository level only: the value to assign. A string, an array of strings, or null to clear the value.",
                    oneOf: [
                      {
                        type: "string",
                      },
                      {
                        items: {
                          type: "string",
                        },
                        type: "array",
                      },
                      {
                        type: "null",
                      },
                    ],
                  },
                },
                required: ["property_name", "value"],
                type: "object",
              },
              {
                additionalProperties: false,
                description:
                  "An organization- or enterprise-level custom property definition.",
                properties: {
                  allowed_values: {
                    anyOf: [
                      {
                        items: {
                          type: "string",
                        },
                        type: "array",
                      },
                      {
                        type: "null",
                      },
                    ],
                    description:
                      "The ordered list of allowed values for single_select and multi_select properties. Omit when updating to preserve the current list; use null or an empty array to clear it.",
                  },
                  default_value: {
                    description:
                      "The value applied when a repository does not set the property. Omit when updating to preserve the current value; use null to clear it.",
                    oneOf: [
                      {
                        type: "string",
                      },
                      {
                        items: {
                          type: "string",
                        },
                        type: "array",
                      },
                      {
                        type: "null",
                      },
                    ],
                  },
                  description: {
                    anyOf: [
                      {
                        type: "string",
                      },
                      {
                        type: "null",
                      },
                    ],
                    description:
                      "A short description of the property. Omit when updating to preserve the current description; use null to clear it.",
                  },
                  property_name: {
                    description: "The name of the custom property.",
                    type: "string",
                  },
                  require_explicit_values: {
                    description:
                      "Whether repositories must explicitly set a value for the property. Omit when updating to preserve the current setting.",
                    type: "boolean",
                  },
                  required: {
                    description:
                      "Whether the property must be set on every repository. Omit when updating to preserve the current setting.",
                    type: "boolean",
                  },
                  value_type: {
                    description:
                      "The data type of the property. Required for new definitions; omit when updating to preserve the current type.",
                    enum: [
                      "string",
                      "single_select",
                      "multi_select",
                      "true_false",
                      "url",
                    ],
                    type: "string",
                  },
                  values_editable_by: {
                    anyOf: [
                      {
                        enum: ["org_actors", "org_and_repo_actors"],
                        type: "string",
                      },
                      {
                        type: "null",
                      },
                    ],
                    description:
                      "Who can edit the values of the property. Omit when updating to preserve the current setting; use null to restore the default.",
                  },
                },
                required: ["property_name"],
                type: "object",
              },
            ],
            type: "object",
          },
          type: "array",
        },
        repo: {
          description: "Repository name. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["level", "properties"],
      type: "object",
    },
    name: "custom_properties_write",
  },
  {
    annotations: {
      destructiveHint: true,
      idempotentHint: false,
      readOnlyHint: false,
      title: "Delete file",
    },
    description: "Delete a file from a GitHub repository",
    inputSchema: {
      properties: {
        branch: {
          description: "Branch to delete the file from",
          type: "string",
        },
        message: {
          description: "Commit message",
          type: "string",
        },
        owner: {
          description: "Repository owner (username or organization)",
          type: "string",
          "x-mcp-header": "owner",
        },
        path: {
          description: "Path to the file to delete",
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "path", "message", "branch"],
      type: "object",
    },
    name: "delete_file",
  },
  {
    annotations: {
      destructiveHint: true,
      idempotentHint: false,
      readOnlyHint: false,
      title: "Manage discussion comments",
    },
    description:
      "Write operations for discussion comments.\nSupports adding top-level comments, replying to existing comments, updating comment content, deleting comments, and marking or unmarking comments as the answer.",
    inputSchema: {
      properties: {
        body: {
          description:
            "Comment content (required for 'add', 'reply', and 'update' methods)",
          type: "string",
        },
        commentNodeID: {
          description:
            "The Node ID of the discussion comment (required for 'reply', 'update', 'delete', 'mark_answer', and 'unmark_answer' methods). For 'reply', this is the top-level comment to reply to; GitHub Discussions only support one level of nesting.",
          type: "string",
        },
        discussionNumber: {
          description:
            "Discussion number (required for 'add' and 'reply' methods)",
          type: "number",
        },
        method: {
          description:
            "Write operation to perform on a discussion comment.\nOptions are:\n- 'add' - adds a new top-level comment to a discussion.\n- 'reply' - replies to a top-level discussion comment (GitHub Discussions only support one level of nesting).\n- 'update' - updates an existing discussion comment.\n- 'delete' - deletes a discussion comment.\n- 'mark_answer' - marks a discussion comment as the answer (Q&A only).\n- 'unmark_answer' - unmarks a discussion comment as the answer (Q&A only).\n",
          enum: [
            "add",
            "reply",
            "update",
            "delete",
            "mark_answer",
            "unmark_answer",
          ],
          type: "string",
        },
        owner: {
          description:
            "Repository owner (required for 'add' and 'reply' methods)",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description:
            "Repository name (required for 'add' and 'reply' methods)",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["method"],
      type: "object",
    },
    name: "discussion_comment_write",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get code quality finding",
    },
    description:
      "Get details of a specific code quality finding in a GitHub repository.",
    inputSchema: {
      properties: {
        findingNumber: {
          description: "The number of the finding.",
          type: "number",
        },
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "findingNumber"],
      type: "object",
    },
    name: "get_code_quality_finding",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get code scanning alert",
    },
    description:
      "Get details of a specific code scanning alert in a GitHub repository.",
    inputSchema: {
      properties: {
        alertNumber: {
          description: "The number of the alert.",
          type: "number",
        },
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "alertNumber"],
      type: "object",
    },
    name: "get_code_scanning_alert",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get commit details",
    },
    description: "Get details for a commit from a GitHub repository",
    inputSchema: {
      properties: {
        detail: {
          default: "stats",
          description:
            'Level of detail to include for changed files. "none" omits stats and files entirely. "stats" (default) includes per-file metadata: filename, status, and lines-of-code counts (additions, deletions, changes), with no patch content. "full_patch" additionally includes the unified diff content for each file and can be very large.',
          enum: ["none", "stats", "full_patch"],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        sha: {
          description: "Commit SHA, branch name, or tag name",
          type: "string",
        },
      },
      required: ["owner", "repo", "sha"],
      type: "object",
    },
    name: "get_commit",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get dependabot alert",
    },
    description:
      "Get details of a specific dependabot alert in a GitHub repository.",
    inputSchema: {
      properties: {
        alertNumber: {
          description: "The number of the alert.",
          type: "number",
        },
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "alertNumber"],
      type: "object",
    },
    name: "get_dependabot_alert",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get discussion",
    },
    description: "Get a specific discussion by ID",
    inputSchema: {
      properties: {
        discussionNumber: {
          description: "Discussion Number",
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "discussionNumber"],
      type: "object",
    },
    name: "get_discussion",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get discussion comments",
    },
    description: "Get comments from a discussion",
    inputSchema: {
      properties: {
        after: {
          description:
            "Cursor for pagination. Use the cursor from the previous response.",
          type: "string",
        },
        discussionNumber: {
          description: "Discussion Number",
          type: "number",
        },
        includeReplies: {
          description:
            "When true, each top-level comment will include its replies nested within it (up to 100 replies per comment, which is the GitHub API maximum). Defaults to false.",
          type: "boolean",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "discussionNumber"],
      type: "object",
    },
    name: "get_discussion_comments",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get file or directory contents",
    },
    description:
      "Get the contents of a file or directory from a GitHub repository",
    inputSchema: {
      properties: {
        fields: {
          description:
            "Subset of fields to return for each entry when the path is a directory. If omitted, all fields are returned. Ignored when the path is a single file. Use this to reduce response size when listing directories and you only need specific fields, e.g. just 'name' and 'type'.",
          items: {
            enum: [
              "type",
              "name",
              "path",
              "size",
              "sha",
              "url",
              "git_url",
              "html_url",
              "download_url",
            ],
            type: "string",
          },
          type: "array",
        },
        owner: {
          description: "Repository owner (username or organization)",
          type: "string",
          "x-mcp-header": "owner",
        },
        path: {
          default: "/",
          description: "Path to file/directory",
          type: "string",
        },
        ref: {
          description:
            "Accepts optional git refs such as `refs/tags/{tag}`, `refs/heads/{branch}` or `refs/pull/{pr_number}/head`",
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        sha: {
          description:
            "Accepts optional commit SHA. If specified, it will be used instead of ref",
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "get_file_contents",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get a global security advisory",
    },
    description: "Get a global security advisory",
    inputSchema: {
      properties: {
        ghsaId: {
          description:
            "GitHub Security Advisory ID (format: GHSA-xxxx-xxxx-xxxx).",
          type: "string",
        },
      },
      required: ["ghsaId"],
      type: "object",
    },
    name: "get_global_security_advisory",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get GitHub Actions workflow job logs",
    },
    description:
      "Get logs for GitHub Actions workflow jobs.\nUse this tool to retrieve logs for a specific job or all failed jobs in a workflow run.\nFor single job logs, provide job_id. For all failed jobs in a run, provide run_id with failed_only=true.\n",
    inputSchema: {
      properties: {
        failed_only: {
          description:
            "When true, gets logs for all failed jobs in the workflow run specified by run_id. Requires run_id to be provided.",
          type: "boolean",
        },
        job_id: {
          description:
            "The unique identifier of the workflow job. Required when getting logs for a single job.",
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        return_content: {
          description: "Returns actual log content instead of URLs",
          type: "boolean",
        },
        run_id: {
          description:
            "The unique identifier of the workflow run. Required when failed_only is true to get logs for all failed jobs in the run.",
          type: "number",
        },
        tail_lines: {
          default: 500,
          description: "Number of lines to return from the end of the log",
          type: "number",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "get_job_logs",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get a specific label from a repository",
    },
    description: "Get a specific label from a repository.",
    inputSchema: {
      properties: {
        name: {
          description: "Label name.",
          type: "string",
        },
        owner: {
          description: "Repository owner (username or organization name)",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "name"],
      type: "object",
    },
    name: "get_label",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get latest release",
    },
    description: "Get the latest release in a GitHub repository",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "get_latest_release",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get a release by tag name",
    },
    description:
      "Get a specific release by its tag name in a GitHub repository",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        tag: {
          description: "Tag name (e.g., 'v1.0.0')",
          type: "string",
        },
      },
      required: ["owner", "repo", "tag"],
      type: "object",
    },
    name: "get_release_by_tag",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get repository tree",
    },
    description:
      "Get the tree structure (files and directories) of a GitHub repository at a specific ref or SHA",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner (username or organization)",
          type: "string",
          "x-mcp-header": "owner",
        },
        path_filter: {
          description:
            "Optional path prefix to filter the tree results (e.g., 'src/' to only show files in the src directory)",
          type: "string",
        },
        recursive: {
          default: false,
          description:
            "Setting this parameter to true returns the objects or subtrees referenced by the tree. Default is false",
          type: "boolean",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        tree_sha: {
          description:
            "The SHA1 value or ref (branch or tag) name of the tree. Defaults to the repository's default branch",
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "get_repository_tree",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get secret scanning alert",
    },
    description:
      "Get details of a specific secret scanning alert in a GitHub repository.",
    inputSchema: {
      properties: {
        alertNumber: {
          description: "The number of the alert.",
          type: "number",
        },
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "alertNumber"],
      type: "object",
    },
    name: "get_secret_scanning_alert",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get tag details",
    },
    description: "Get details about a specific git tag in a GitHub repository",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        tag: {
          description: "Tag name",
          type: "string",
        },
      },
      required: ["owner", "repo", "tag"],
      type: "object",
    },
    name: "get_tag",
  },
  {
    annotations: {
      idempotentHint: false,
      openWorldHint: true,
      readOnlyHint: true,
      title: "GitHub Support Docs Search",
    },
    description:
      "Retrieve documentation relevant to answer GitHub product and support questions. Support topics include: GitHub Actions Workflows, Authentication, GitHub Support Inquiries, Pull Request Practices, Repository Maintenance, GitHub Pages, GitHub Packages, GitHub Discussions, Copilot Spaces",
    inputSchema: {
      properties: {
        query: {
          description:
            "Input from the user about the question they need answered. This is the latest raw unedited user message. You should ALWAYS leave the user message as it is, you should never modify it.",
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "github_support_docs_search",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get issue details",
    },
    description:
      "Get information about a specific issue in a GitHub repository.",
    inputSchema: {
      properties: {
        issue_number: {
          description: "The number of the issue",
          type: "number",
        },
        method: {
          description:
            "The read operation to perform on a single issue.\nOptions are:\n1. get - Get issue details. Also returns best-effort hierarchy flags (`has_parent`, `has_children`); `parent` and `sub_issues_summary` are optional relationship summaries, and `closed_by_pull_requests` summarizes the pull requests configured to close the issue as `total_count` plus up to 5 `references`.\n2. get_comments - Get issue comments.\n3. get_sub_issues - Get sub-issues (children) of the issue.\n4. get_parent - Get the parent issue, if this issue is a sub-issue of another.\n5. get_labels - Get labels assigned to the issue.\n",
          enum: [
            "get",
            "get_comments",
            "get_sub_issues",
            "get_parent",
            "get_labels",
          ],
          type: "string",
        },
        owner: {
          description: "The owner of the repository",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "The name of the repository",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["method", "owner", "repo", "issue_number"],
      type: "object",
    },
    name: "issue_read",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Create or update issue/pull request",
    },
    description:
      "Create a new or update an existing issue in a GitHub repository.",
    inputSchema: {
      properties: {
        assignees: {
          description: "Usernames to assign to this issue",
          items: {
            type: "string",
          },
          type: "array",
        },
        body: {
          description: "Issue body content",
          type: "string",
        },
        duplicate_of: {
          description:
            "Issue number that this issue is a duplicate of. Required when state_reason is 'duplicate'.",
          type: "number",
        },
        issue_fields: {
          description:
            "Issue field values to set or clear. Each item requires 'field_name' and exactly one of 'value', 'field_option_name', or 'delete: true'.",
          items: {
            additionalProperties: false,
            properties: {
              delete: {
                description:
                  "Set to true to clear this field's current value on the issue. When false or omitted, this property is ignored. Cannot be true when 'value' or 'field_option_name' is provided.",
                type: "boolean",
              },
              field_name: {
                description:
                  "Issue field name (case-insensitive). Must match a field returned by list_issue_fields for this repository or its organization.",
                type: "string",
              },
              field_option_name: {
                description:
                  "Option name for single-select fields. Validated against the field's options before the API call. Cannot be combined with 'value' or 'delete: true'.",
                type: "string",
              },
              value: {
                description:
                  "Value to set. Use for text, number, and date fields (date as YYYY-MM-DD). For single-select fields, prefer 'field_option_name' so the option is validated before the API call. Cannot be combined with 'field_option_name' or 'delete: true'.",
                type: ["string", "number", "boolean"],
              },
            },
            required: ["field_name"],
            type: "object",
          },
          type: "array",
        },
        issue_number: {
          description: "Issue number to update",
          type: "number",
        },
        labels: {
          description: "Labels to apply to this issue",
          items: {
            type: "string",
          },
          type: "array",
        },
        method: {
          description:
            "Write operation to perform on a single issue.\nOptions are:\n- 'create' - creates a new issue.\n- 'update' - updates an existing issue.\n",
          enum: ["create", "update"],
          type: "string",
        },
        milestone: {
          description: "Milestone number",
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        parent_issue_number: {
          description:
            "Issue number of the parent issue. Only used when method is 'create' and cannot be combined with issue_fields. The new issue is created and attached to this parent in the same operation.",
          minimum: 1,
          type: "number",
        },
        parent_owner: {
          description:
            "Repository owner of the parent issue. Must be provided with parent_repo. Omit both to use owner and repo. Only used when method is 'create' and parent_issue_number is provided.",
          type: "string",
        },
        parent_repo: {
          description:
            "Repository name of the parent issue. Must be provided with parent_owner. Omit both to use owner and repo. Only used when method is 'create' and parent_issue_number is provided.",
          type: "string",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        state: {
          description: "New state",
          enum: ["open", "closed"],
          type: "string",
        },
        state_reason: {
          description:
            "Reason for the state change. Ignored unless state is changed.",
          enum: ["completed", "not_planned", "duplicate"],
          type: "string",
        },
        title: {
          description: "Issue title",
          type: "string",
        },
        type: {
          anyOf: [
            {
              minLength: 1,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          description:
            "Type of this issue. For updates, pass null to remove the current type. Only use if issue types are enabled for this repository. Use list_issue_types to get valid type values for this repository or its owner organization. If the repository doesn't support issue types, omit this parameter.",
        },
      },
      required: ["method", "owner", "repo"],
      type: "object",
    },
    name: "issue_write",
  },
  {
    annotations: {
      destructiveHint: true,
      idempotentHint: false,
      readOnlyHint: false,
      title: "Write operations on repository labels",
    },
    description:
      "Perform write operations on repository labels. To set labels on issues, use the 'update_issue' tool.",
    inputSchema: {
      properties: {
        color: {
          description:
            "Label color as 6-character hex code without '#' prefix (e.g., 'f29513'). Required for 'create', optional for 'update'.",
          type: "string",
        },
        description: {
          description:
            "Label description text. Optional for 'create' and 'update'.",
          type: "string",
        },
        method: {
          description: "Operation to perform: 'create', 'update', or 'delete'",
          enum: ["create", "update", "delete"],
          type: "string",
        },
        name: {
          description: "Label name - required for all operations",
          type: "string",
        },
        new_name: {
          description:
            "New name for the label (used only with 'update' method to rename)",
          type: "string",
        },
        owner: {
          description: "Repository owner (username or organization name)",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["method", "owner", "repo", "name"],
      type: "object",
    },
    name: "label_write",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List branches",
    },
    description: "List branches in a GitHub repository",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_branches",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List code scanning alerts",
    },
    description: "List code scanning alerts in a GitHub repository.",
    inputSchema: {
      properties: {
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        ref: {
          description: "The Git reference for the results you want to list.",
          type: "string",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
        severity: {
          description: "Filter code scanning alerts by severity",
          enum: [
            "critical",
            "high",
            "medium",
            "low",
            "warning",
            "note",
            "error",
          ],
          type: "string",
        },
        state: {
          default: "open",
          description: "Filter code scanning alerts by state. Defaults to open",
          enum: ["open", "closed", "dismissed", "fixed"],
          type: "string",
        },
        tool_name: {
          description: "The name of the tool used for code scanning.",
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_code_scanning_alerts",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List commits",
    },
    description:
      "Get list of commits of a branch in a GitHub repository. Returns at least 30 results per page by default, but can return more if specified using the perPage parameter (up to 100).",
    inputSchema: {
      properties: {
        author: {
          description: "Author username or email address to filter commits by",
          type: "string",
        },
        fields: {
          description:
            "Subset of fields to return for each commit. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields, e.g. just 'sha' and 'html_url'.",
          items: {
            enum: ["sha", "html_url", "commit", "author", "committer"],
            type: "string",
          },
          type: "array",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        path: {
          description:
            "Only commits containing this file path will be returned",
          type: "string",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        sha: {
          description:
            "Commit SHA, branch or tag name to list commits of. If not provided, uses the default branch of the repository. If a commit SHA is provided, will list commits up to that SHA.",
          type: "string",
        },
        since: {
          description:
            "Only commits after this date will be returned (ISO 8601 format: YYYY-MM-DDTHH:MM:SSZ or YYYY-MM-DD)",
          type: "string",
        },
        until: {
          description:
            "Only commits before this date will be returned (ISO 8601 format: YYYY-MM-DDTHH:MM:SSZ or YYYY-MM-DD)",
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_commits",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List dependabot alerts",
    },
    description: "List dependabot alerts in a GitHub repository.",
    inputSchema: {
      properties: {
        after: {
          description:
            "Cursor for pagination. Use the cursor from the previous response.",
          type: "string",
        },
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
        severity: {
          description: "Filter dependabot alerts by severity",
          enum: ["low", "medium", "high", "critical"],
          type: "string",
        },
        state: {
          default: "open",
          description: "Filter dependabot alerts by state. Defaults to open",
          enum: ["open", "fixed", "dismissed", "auto_dismissed"],
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_dependabot_alerts",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List discussion categories",
    },
    description:
      "List discussion categories with their id and name, for a repository or organisation.",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description:
            "Repository name. If not provided, discussion categories will be queried at the organisation level.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner"],
      type: "object",
    },
    name: "list_discussion_categories",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List discussions",
    },
    description: "List discussions for a repository or organisation.",
    inputSchema: {
      properties: {
        after: {
          description:
            "Cursor for pagination. Use the cursor from the previous response.",
          type: "string",
        },
        category: {
          description:
            "Optional filter by discussion category ID. If provided, only discussions with this category are listed.",
          type: "string",
        },
        direction: {
          description: "Order direction.",
          enum: ["ASC", "DESC"],
          type: "string",
        },
        orderBy: {
          description:
            "Order discussions by field. If provided, the 'direction' also needs to be provided.",
          enum: ["CREATED_AT", "UPDATED_AT"],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description:
            "Repository name. If not provided, discussions will be queried at the organisation level.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner"],
      type: "object",
    },
    name: "list_discussions",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List global security advisories",
    },
    description: "List global security advisories from GitHub.",
    inputSchema: {
      properties: {
        affects: {
          description:
            'Filter advisories by affected package or version (e.g. "package1,package2@1.0.0").',
          type: "string",
        },
        cveId: {
          description: "Filter by CVE ID.",
          type: "string",
        },
        cwes: {
          description:
            'Filter by Common Weakness Enumeration IDs (e.g. ["79", "284", "22"]).',
          items: {
            type: "string",
          },
          type: "array",
        },
        ecosystem: {
          description: "Filter by package ecosystem.",
          enum: [
            "actions",
            "composer",
            "erlang",
            "go",
            "maven",
            "npm",
            "nuget",
            "other",
            "pip",
            "pub",
            "rubygems",
            "rust",
          ],
          type: "string",
        },
        ghsaId: {
          description:
            "Filter by GitHub Security Advisory ID (format: GHSA-xxxx-xxxx-xxxx).",
          type: "string",
        },
        isWithdrawn: {
          description: "Whether to only return withdrawn advisories.",
          type: "boolean",
        },
        modified: {
          description:
            "Filter by publish or update date or date range (ISO 8601 date or range).",
          type: "string",
        },
        published: {
          description:
            "Filter by publish date or date range (ISO 8601 date or range).",
          type: "string",
        },
        severity: {
          description: "Filter by severity.",
          enum: ["unknown", "low", "medium", "high", "critical"],
          type: "string",
        },
        type: {
          default: "reviewed",
          description: "Advisory type.",
          enum: ["reviewed", "malware", "unreviewed"],
          type: "string",
        },
        updated: {
          description:
            "Filter by update date or date range (ISO 8601 date or range).",
          type: "string",
        },
      },
      type: "object",
    },
    name: "list_global_security_advisories",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List issue fields",
    },
    description:
      "List issue fields for a repository or organization. Returns field definitions including name, type (text, number, date, single_select), and for single_select fields the list of valid option names. When repo is omitted, returns org-level fields directly.",
    inputSchema: {
      properties: {
        owner: {
          description:
            "The account owner of the repository or organization. The name is not case sensitive.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description:
            "The name of the repository. When provided, returns fields for this specific repository (inherited from its organization). When omitted, returns org-level fields directly.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner"],
      type: "object",
    },
    name: "list_issue_fields",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List available issue types",
    },
    description:
      "List supported issue types for a repository or its owner organization. When repo is omitted, returns org-level issue types directly.",
    inputSchema: {
      properties: {
        owner: {
          description: "The account owner of the repository or organization.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description:
            "The name of the repository. When provided, returns issue types for this specific repository. When omitted, returns org-level issue types directly.",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner"],
      type: "object",
    },
    name: "list_issue_types",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List issues",
    },
    description:
      "List issues in a GitHub repository. For pagination, use the 'endCursor' from the previous response's 'pageInfo' in the 'after' parameter.",
    inputSchema: {
      properties: {
        after: {
          description:
            "Cursor for pagination. Use the cursor from the previous response.",
          type: "string",
        },
        direction: {
          description:
            "Order direction. If provided, the 'orderBy' also needs to be provided.",
          enum: ["ASC", "DESC"],
          type: "string",
        },
        field_filters: {
          description:
            "Filter by custom issue field values. Each entry takes a field_name and a value; the server looks up the field and coerces the value to its type (single-select option name, text, number, or YYYY-MM-DD date).",
          items: {
            properties: {
              field_name: {
                description:
                  'Name of the custom field (e.g. "Priority"). Case-insensitive.',
                type: "string",
              },
              value: {
                description:
                  'Value to filter on. For single-select fields, the option name (e.g. "P1"). For dates, YYYY-MM-DD. For numbers, the numeric value as a string. For text, the text value.',
                type: "string",
              },
            },
            required: ["field_name", "value"],
            type: "object",
          },
          type: "array",
        },
        fields: {
          description:
            "Subset of fields to return for each issue. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields; omitting 'body' and 'field_values' in particular drops the largest per-result data.",
          items: {
            enum: [
              "number",
              "title",
              "body",
              "state",
              "user",
              "labels",
              "assignees",
              "comments",
              "created_at",
              "updated_at",
              "field_values",
            ],
            type: "string",
          },
          type: "array",
        },
        labels: {
          description: "Filter by labels",
          items: {
            type: "string",
          },
          type: "array",
        },
        orderBy: {
          description:
            "Order issues by field. If provided, the 'direction' also needs to be provided.",
          enum: ["CREATED_AT", "UPDATED_AT", "COMMENTS"],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        since: {
          description: "Filter by date (ISO 8601 timestamp)",
          type: "string",
        },
        state: {
          description:
            "Filter by state, by default both open and closed issues are returned when not provided",
          enum: ["OPEN", "CLOSED"],
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_issues",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List labels from a repository",
    },
    description:
      "List labels from a repository, ordered by issue count (descending) so the most-used labels are returned first. For pagination, use the 'endCursor' from the previous response's 'pageInfo' in the 'after' parameter.",
    inputSchema: {
      properties: {
        after: {
          description:
            "Cursor for pagination. Use the cursor from the previous response.",
          type: "string",
        },
        owner: {
          description:
            "Repository owner (username or organization name) - required for all operations",
          type: "string",
          "x-mcp-header": "owner",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name - required for all operations",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_label",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List pull requests",
    },
    description:
      "List pull requests in a GitHub repository. If the user specifies an author, then DO NOT use this tool and use the search_pull_requests tool instead.",
    inputSchema: {
      properties: {
        base: {
          description: "Filter by base branch",
          type: "string",
        },
        direction: {
          description: "Sort direction",
          enum: ["asc", "desc"],
          type: "string",
        },
        fields: {
          description:
            "Subset of fields to return for each pull request. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields; omitting 'body' in particular drops the largest per-result data.",
          items: {
            enum: [
              "number",
              "title",
              "body",
              "state",
              "draft",
              "merged",
              "mergeable_state",
              "html_url",
              "user",
              "labels",
              "assignees",
              "requested_reviewers",
              "merged_by",
              "head",
              "base",
              "additions",
              "deletions",
              "changed_files",
              "commits",
              "comments",
              "created_at",
              "updated_at",
              "closed_at",
              "merged_at",
              "milestone",
            ],
            type: "string",
          },
          type: "array",
        },
        head: {
          description: "Filter by head user/org and branch",
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        sort: {
          description: "Sort by",
          enum: ["created", "updated", "popularity", "long-running"],
          type: "string",
        },
        state: {
          description: "Filter by state",
          enum: ["open", "closed", "all"],
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_pull_requests",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List releases",
    },
    description: "List releases in a GitHub repository",
    inputSchema: {
      properties: {
        fields: {
          description:
            "Subset of fields to return for each release. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields; omitting 'body' in particular drops the largest per-release data.",
          items: {
            enum: [
              "id",
              "tag_name",
              "name",
              "body",
              "html_url",
              "published_at",
              "prerelease",
              "draft",
              "author",
            ],
            type: "string",
          },
          type: "array",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_releases",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List repository collaborators",
    },
    description:
      "List collaborators of a GitHub repository. Results are paginated; the response includes `nextPage`, `prevPage`, `firstPage`, and `lastPage` fields. To get the next page, use the `nextPage` value as the `page` parameter.",
    inputSchema: {
      properties: {
        affiliation: {
          description:
            "Filter by affiliation. Can be one of: 'outside' (outside collaborators), 'direct' (all with permissions regardless of org membership), 'all' (all collaborators). Default: 'all'",
          enum: ["outside", "direct", "all"],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (default 1, min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description:
            "Results per page for pagination (default 30, min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_repository_collaborators",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List repository security advisories",
    },
    description: "List repository security advisories for a GitHub repository.",
    inputSchema: {
      properties: {
        direction: {
          description: "Sort direction.",
          enum: ["asc", "desc"],
          type: "string",
        },
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
        sort: {
          description: "Sort field.",
          enum: ["created", "updated", "published"],
          type: "string",
        },
        state: {
          description: "Filter by advisory state.",
          enum: ["triage", "draft", "published", "closed"],
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_repository_security_advisories",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List secret scanning alerts",
    },
    description: "List secret scanning alerts in a GitHub repository.",
    inputSchema: {
      properties: {
        owner: {
          description: "The owner of the repository.",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "The name of the repository.",
          type: "string",
          "x-mcp-header": "repo",
        },
        resolution: {
          description: "Filter by resolution",
          enum: [
            "false_positive",
            "wont_fix",
            "revoked",
            "pattern_edited",
            "pattern_deleted",
            "used_in_tests",
          ],
          type: "string",
        },
        secret_type: {
          description:
            "A comma-separated list of secret types to return. All default secret patterns are returned. To return generic patterns, pass the token name(s) in the parameter.",
          type: "string",
        },
        state: {
          description: "Filter by state",
          enum: ["open", "resolved"],
          type: "string",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_secret_scanning_alerts",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "List tags",
    },
    description: "List git tags in a GitHub repository",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo"],
      type: "object",
    },
    name: "list_tags",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Merge pull request",
    },
    description: "Merge a pull request in a GitHub repository.",
    inputSchema: {
      properties: {
        commit_message: {
          description: "Extra detail for merge commit",
          type: "string",
        },
        commit_title: {
          description: "Title for merge commit",
          type: "string",
        },
        expectedHeadSha: {
          description: "The expected SHA of the pull request's HEAD ref",
          type: "string",
        },
        merge_method: {
          description: "Merge method",
          enum: ["merge", "squash", "rebase"],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        pullNumber: {
          description: "Pull request number",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "pullNumber"],
      type: "object",
    },
    name: "merge_pull_request",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Get details for a single pull request",
    },
    description:
      "Get information on a specific pull request in GitHub repository.",
    inputSchema: {
      properties: {
        after: {
          description:
            "Cursor for pagination, used only by the get_review_comments method. Pass the endCursor from the previous page's PageInfo to fetch the next page.",
          type: "string",
        },
        method: {
          description:
            "Action to specify what pull request data needs to be retrieved from GitHub. \nPossible options: \n 1. get - Get details of a specific pull request.\n 2. get_diff - Get the diff of a pull request.\n 3. get_status - Get combined commit status of a head commit in a pull request.\n 4. get_files - Get the list of files changed in a pull request. Use with pagination parameters to control the number of results returned.\n 5. get_commits - Get the list of commits on a pull request. Use with pagination parameters to control the number of results returned.\n 6. get_review_comments - Get review threads on a pull request. Each thread contains logically grouped review comments made on the same code location during pull request reviews. Returns thread metadata and comments with nullable current and original line-range coordinates (line, start_line, original_line, original_start_line). Current coordinates are omitted when unavailable, such as for outdated comments. Use cursor-based pagination (perPage, after) to control results.\n 7. get_reviews - Get the reviews on a pull request. When asked for review comments, use get_review_comments method. Use with pagination parameters to control the number of results returned.\n 8. get_comments - Get comments on a pull request. Use this if user doesn't specifically want review comments. Use with pagination parameters to control the number of results returned.\n 9. get_check_runs - Get check runs for the head commit of a pull request. Check runs are the individual CI/CD jobs and checks that run on the PR.\n",
          enum: [
            "get",
            "get_diff",
            "get_status",
            "get_files",
            "get_commits",
            "get_review_comments",
            "get_reviews",
            "get_comments",
            "get_check_runs",
          ],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        pullNumber: {
          description: "Pull request number",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["method", "owner", "repo", "pullNumber"],
      type: "object",
    },
    name: "pull_request_read",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title:
        "Write operations (create, submit, delete) on pull request reviews",
    },
    description:
      'Create and/or submit, delete review of a pull request.\n\nAvailable methods:\n- create: Create a new review of a pull request. If "event" parameter is provided, the review is submitted. If "event" is omitted, a pending review is created.\n- submit_pending: Submit an existing pending review of a pull request. This requires that a pending review exists for the current user on the specified pull request. The "body" and "event" parameters are used when submitting the review.\n- delete_pending: Delete an existing pending review of a pull request. This requires that a pending review exists for the current user on the specified pull request.\n- resolve_thread: Resolve a review thread. Requires only "threadId" parameter with the thread\'s node ID (e.g., PRRT_kwDOxxx). The owner, repo, and pullNumber parameters are not used for this method. Resolving an already-resolved thread is a no-op.\n- unresolve_thread: Unresolve a previously resolved review thread. Requires only "threadId" parameter. The owner, repo, and pullNumber parameters are not used for this method. Unresolving an already-unresolved thread is a no-op.\n',
    inputSchema: {
      properties: {
        body: {
          description: "Review comment text",
          type: "string",
        },
        commitID: {
          description: "SHA of commit to review",
          type: "string",
        },
        event: {
          description: "Review action to perform.",
          enum: ["APPROVE", "REQUEST_CHANGES", "COMMENT"],
          type: "string",
        },
        method: {
          description: "The write operation to perform on pull request review.",
          enum: [
            "create",
            "submit_pending",
            "delete_pending",
            "resolve_thread",
            "unresolve_thread",
          ],
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        pullNumber: {
          description: "Pull request number",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        threadId: {
          description:
            "The node ID of the review thread (e.g., PRRT_kwDOxxx). Required for resolve_thread and unresolve_thread methods. Get thread IDs from pull_request_read with method get_review_comments.",
          type: "string",
        },
      },
      required: ["method", "owner", "repo", "pullNumber"],
      type: "object",
    },
    name: "pull_request_review_write",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Push files to repository",
    },
    description:
      "Push multiple files to a GitHub repository in a single commit",
    inputSchema: {
      properties: {
        branch: {
          description: "Branch to push to",
          type: "string",
        },
        files: {
          description:
            "Array of file objects to push, each object with path (string) and content (string)",
          items: {
            additionalProperties: false,
            properties: {
              content: {
                description: "file content",
                type: "string",
              },
              path: {
                description: "path to the file",
                type: "string",
              },
            },
            required: ["path", "content"],
            type: "object",
          },
          type: "array",
        },
        message: {
          description: "Commit message",
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "branch", "files", "message"],
      type: "object",
    },
    name: "push_files",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Read repository rulesets",
    },
    description:
      "Read rulesets at the repository, organization, or enterprise level, and rule suites at the repository or organization level. Select the level with the 'level' parameter and the operation with the 'method' parameter.",
    inputSchema: {
      properties: {
        actor_name: {
          description:
            "The handle for the GitHub user account to filter rule suites on. Used by the 'list_rule_suites' method.",
          type: "string",
        },
        branch: {
          description:
            "Branch name. Required for the 'get_rules_for_branch' method.",
          type: "string",
        },
        enterprise: {
          description: "Enterprise slug. Required when level is 'enterprise'.",
          type: "string",
        },
        evaluate_status: {
          description:
            "Filter rule suites by ruleset evaluation mode. Used by the 'list_rule_suites' method.",
          enum: ["all", "active", "evaluate"],
          type: "string",
        },
        includes_parents: {
          description:
            "Include rulesets configured at higher levels that also apply. Defaults to true. Used by the 'get' and 'list' methods at the repository level.",
          type: "boolean",
        },
        level: {
          description:
            "The level at which the ruleset is configured:\n- 'repository': A ruleset on a single repository (requires 'owner' and 'repo').\n- 'organization': A ruleset covering repositories in an organization (requires 'org').\n- 'enterprise': A ruleset covering repositories across an enterprise (requires 'enterprise').",
          enum: ["repository", "organization", "enterprise"],
          type: "string",
        },
        method: {
          description:
            "Operation to perform:\n- 'get': Get a specific ruleset by ID (requires 'ruleset_id'). Supported at every level.\n- 'list': List all rulesets. Supported at every level.\n- 'get_rules_for_branch': Get all rules that apply to a branch (requires 'branch'). Repository level only.\n- 'list_rule_suites': List rule suites, the evaluations of rules against pushes. Repository and organization levels only.\n- 'get_rule_suite': Get a specific rule suite by ID (requires 'rule_suite_id'). Repository and organization levels only.",
          enum: [
            "get",
            "list",
            "get_rules_for_branch",
            "list_rule_suites",
            "get_rule_suite",
          ],
          type: "string",
        },
        org: {
          description:
            "Organization name. Required when level is 'organization'.",
          type: "string",
        },
        owner: {
          description: "Repository owner. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        ref: {
          description:
            "The name of the ref (branch, tag, etc.) to filter rule suites by. Used by the 'list_rule_suites' method.",
          type: "string",
        },
        repo: {
          description: "Repository name. Required when level is 'repository'.",
          type: "string",
          "x-mcp-header": "repo",
        },
        repository_name: {
          description:
            "Repository name to filter rule suites by. Used by the 'list_rule_suites' method at the organization level.",
          type: "string",
        },
        rule_suite_id: {
          description:
            "Rule suite ID. Required for the 'get_rule_suite' method.",
          type: "number",
        },
        rule_suite_result: {
          description:
            "The rule suite result to filter by. Used by the 'list_rule_suites' method.",
          enum: ["pass", "fail", "bypass", "all"],
          type: "string",
        },
        ruleset_id: {
          description: "Ruleset ID. Required for the 'get' method.",
          type: "number",
        },
        time_period: {
          description:
            "The time period to filter rule suites by. Used by the 'list_rule_suites' method.",
          enum: ["hour", "day", "week", "month"],
          type: "string",
        },
      },
      required: ["level", "method"],
      type: "object",
    },
    name: "repository_ruleset_read",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Request Copilot review",
    },
    description:
      "Request a GitHub Copilot code review for a pull request. Use this for automated feedback on pull requests, usually before requesting a human reviewer.",
    inputSchema: {
      properties: {
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        pullNumber: {
          description: "Pull request number",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "pullNumber"],
      type: "object",
    },
    name: "request_copilot_review",
  },
  {
    annotations: {
      idempotentHint: false,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Run Secret Scanning",
    },
    description:
      "Scan files, content, or recent changes for secrets such as API keys, passwords, tokens, and credentials.\n\nThis tool is intended for targeted scans of specific files, snippets, or diffs provided directly as content. The files parameter accepts either a single string or an array of strings containing raw file contents or diff hunks, and returns detected secrets with their locations and related secret scanning metadata. Content must not be empty. For full repository scanning, other mechanisms are available.\n\nCaveats:\n\n- Only files within the codebase should be scanned. Files outside of the codebase should not be sent.\n- Files listed in .gitignore should be skipped.",
    inputSchema: {
      properties: {
        files: {
          anyOf: [
            {
              minLength: 1,
              type: "string",
            },
            {
              items: {
                type: "string",
              },
              maxItems: 100,
              minItems: 1,
              type: "array",
            },
          ],
          description:
            "A single string or an array of strings containing file contents, snippets, or diff hunks to scan for secrets. These must be raw contents, not repository file paths.",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["files", "owner", "repo"],
      type: "object",
    },
    name: "run_secret_scanning",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search code",
    },
    description:
      "Fast and precise code search across ALL GitHub repositories using GitHub's native search engine. Best for finding exact symbols, functions, classes, or specific code patterns.",
    inputSchema: {
      properties: {
        fields: {
          description:
            "Subset of fields to return for each code search result. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields; omitting 'repository' and 'text_matches' in particular drops the largest per-result data.",
          items: {
            enum: ["name", "path", "sha", "repository", "text_matches"],
            type: "string",
          },
          type: "array",
        },
        order: {
          description: "Sort order for results",
          enum: ["asc", "desc"],
          type: "string",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description:
            'Search query (GitHub code search REST). Implicit AND between terms; supports `OR`, `NOT`, and `"quoted phrase"` for exact match. Qualifiers: `repo:owner/repo`, `org:`, `user:`, `language:`, `path:dir` (prefix match), `filename:exact.ext`, `extension:`, `in:file`, `in:path`, `size:`, `is:archived`, `is:fork`. Max 256 chars. Examples: `WithContext language:go org:github`; `"package main" repo:o/r`; `func extension:go path:cmd repo:o/r`; `NOT TODO language:go repo:o/r`.',
          type: "string",
        },
        sort: {
          description: "Sort field ('indexed' only)",
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_code",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search commits",
    },
    description:
      "Search for commits across GitHub repositories using GitHub's commit search syntax. Useful for finding specific changes, authors, or messages across one or many repositories. Searches the default branch only.",
    inputSchema: {
      properties: {
        order: {
          description: "Sort order",
          enum: ["asc", "desc"],
          type: "string",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description:
            'Commit search query (GitHub commit search REST). Searches commit messages on the default branch only. Scope the search with `repo:owner/repo`, `org:`, or `user:` (queries without a scope qualifier match across all of GitHub and are usually not what you want). Other qualifiers: `author:`, `committer:`, `author-name:`, `committer-name:`, `author-email:`, `committer-email:`, `author-date:`, `committer-date:` (supports `>`, `<`, `>=`, `<=`, and `YYYY-MM-DD..YYYY-MM-DD` ranges), `merge:true|false`, `hash:`, `tree:`, `parent:`, `is:public`. Examples: `repo:owner/repo fix panic`; `org:github author:defunkt committer-date:>=2024-01-01`; `"refactor cache" repo:o/r`; `hash:abc1234 repo:o/r`.',
          type: "string",
        },
        sort: {
          description:
            "Sort by author or committer date (defaults to best match)",
          enum: ["author-date", "committer-date"],
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_commits",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search issues",
    },
    description:
      'Search issues using natural-language semantic matching. Best for conceptual or paraphrased queries (e.g. "login fails after password reset"). Already scoped to is:issue.',
    inputSchema: {
      properties: {
        fields: {
          description:
            "Subset of fields to return for each issue result. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields; omitting 'body', 'reactions', and 'labels' in particular drops the largest per-result data.",
          items: {
            enum: [
              "number",
              "title",
              "body",
              "state",
              "state_reason",
              "draft",
              "locked",
              "html_url",
              "user",
              "author_association",
              "labels",
              "assignee",
              "assignees",
              "milestone",
              "comments",
              "reactions",
              "created_at",
              "updated_at",
              "closed_at",
              "closed_by",
              "type",
              "repository_url",
              "pull_request",
              "field_values",
            ],
            type: "string",
          },
          type: "array",
        },
        order: {
          description: "Sort order",
          enum: ["asc", "desc"],
          type: "string",
        },
        owner: {
          description:
            "Optional repository owner. If provided with repo, only issues for this repository are listed.",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description:
            "The search query, as natural language. When the user gives alternative wordings, include them as plain words rather than joining them with OR.",
          type: "string",
        },
        repo: {
          description:
            "Optional repository name. If provided with owner, only issues for this repository are listed.",
          type: "string",
          "x-mcp-header": "repo",
        },
        sort: {
          description:
            "Sort field by number of matches of categories, defaults to best match",
          enum: [
            "comments",
            "reactions",
            "reactions-+1",
            "reactions--1",
            "reactions-smile",
            "reactions-thinking_face",
            "reactions-heart",
            "reactions-tada",
            "interactions",
            "created",
            "updated",
          ],
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_issues",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search organizations",
    },
    description:
      "Find GitHub organizations by name, location, or other organization metadata. Ideal for discovering companies, open source foundations, or teams.",
    inputSchema: {
      properties: {
        order: {
          description: "Sort order",
          enum: ["asc", "desc"],
          type: "string",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description:
            "Organization search query. Examples: 'microsoft', 'location:california', 'created:>=2025-01-01'. Search is automatically scoped to type:org.",
          type: "string",
        },
        sort: {
          description: "Sort field by category",
          enum: ["followers", "repositories", "joined"],
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_orgs",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search pull requests",
    },
    description:
      "Search for pull requests in GitHub repositories using issues search syntax already scoped to is:pr",
    inputSchema: {
      properties: {
        fields: {
          description:
            "Subset of fields to return for each pull request result. If omitted, all fields are returned. Use this to reduce response size when you only need specific fields; omitting 'body', 'reactions', and 'labels' in particular drops the largest per-result data.",
          items: {
            enum: [
              "number",
              "title",
              "body",
              "state",
              "state_reason",
              "draft",
              "locked",
              "html_url",
              "user",
              "author_association",
              "labels",
              "assignee",
              "assignees",
              "milestone",
              "comments",
              "reactions",
              "created_at",
              "updated_at",
              "closed_at",
              "closed_by",
              "pull_request",
              "repository_url",
            ],
            type: "string",
          },
          type: "array",
        },
        order: {
          description: "Sort order",
          enum: ["asc", "desc"],
          type: "string",
        },
        owner: {
          description:
            "Optional repository owner. If provided with repo, only pull requests for this repository are listed.",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description: "Search query using GitHub pull request search syntax",
          type: "string",
        },
        repo: {
          description:
            "Optional repository name. If provided with owner, only pull requests for this repository are listed.",
          type: "string",
          "x-mcp-header": "repo",
        },
        sort: {
          description:
            "Sort field by number of matches of categories, defaults to best match",
          enum: [
            "comments",
            "reactions",
            "reactions-+1",
            "reactions--1",
            "reactions-smile",
            "reactions-thinking_face",
            "reactions-heart",
            "reactions-tada",
            "interactions",
            "created",
            "updated",
          ],
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_pull_requests",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search repositories",
    },
    description:
      "Find GitHub repositories by name, description, readme, topics, or other metadata. Perfect for discovering projects, finding examples, or locating specific repositories across GitHub.",
    inputSchema: {
      properties: {
        minimal_output: {
          default: true,
          description:
            "Return minimal repository information (default: true). When false, modern clients receive additional curated repository details, not the complete GitHub API object. Legacy clients retain full GitHub API repository objects.",
          type: "boolean",
        },
        order: {
          description: "Sort order",
          enum: ["asc", "desc"],
          type: "string",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description:
            "Repository search query. Examples: 'machine learning in:name stars:>1000 language:python', 'topic:react', 'user:facebook'. Supports advanced search syntax for precise filtering.",
          type: "string",
        },
        sort: {
          description: "Sort repositories by field, defaults to best match",
          enum: ["stars", "forks", "help-wanted-issues", "updated"],
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_repositories",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: true,
      title: "Search users",
    },
    description:
      "Find GitHub users by username, real name, or other profile information. Useful for locating developers, contributors, or team members.",
    inputSchema: {
      properties: {
        order: {
          description: "Sort order",
          enum: ["asc", "desc"],
          type: "string",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        perPage: {
          description: "Results per page for pagination (min 1, max 100)",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        query: {
          description:
            "User search query. Examples: 'john smith', 'location:seattle', 'followers:>100'. Search is automatically scoped to type:user.",
          type: "string",
        },
        sort: {
          description:
            "Sort users by number of followers or repositories, or when the person joined GitHub.",
          enum: ["followers", "repositories", "joined"],
          type: "string",
        },
      },
      required: ["query"],
      type: "object",
    },
    name: "search_users",
  },
  {
    annotations: {
      idempotentHint: false,
      openWorldHint: true,
      readOnlyHint: true,
      title: "Semantic Issue Similarity Search",
    },
    description:
      "Find issues that are semantically similar to a given issue using natural language understanding. Useful for identifying duplicates, related issues, or similar problems.",
    inputSchema: {
      properties: {
        issue_number: {
          description: "The issue number to find similar issues for",
          minimum: 1,
          type: "number",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        page: {
          description: "Page number for pagination (min 1)",
          minimum: 1,
          type: "number",
        },
        per_page: {
          description:
            "Number of similar issues to return per page (default: 3, max: 10)",
          maximum: 10,
          minimum: 1,
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        threshold: {
          description:
            "Similarity threshold between 0.0 and 1.0 (default: 0.80)",
          maximum: 1,
          minimum: 0,
          type: "number",
        },
      },
      required: ["owner", "repo", "issue_number"],
      type: "object",
    },
    name: "semantic_issue_similarity_search",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Change sub-issue",
    },
    description: "Add a sub-issue to a parent issue in a GitHub repository.",
    inputSchema: {
      properties: {
        after_id: {
          description:
            "The ID of the sub-issue to be prioritized after (either after_id OR before_id should be specified)",
          type: "number",
        },
        before_id: {
          description:
            "The ID of the sub-issue to be prioritized before (either after_id OR before_id should be specified)",
          type: "number",
        },
        issue_number: {
          description: "The number of the parent issue",
          type: "number",
        },
        method: {
          description:
            "The action to perform on a single sub-issue\nOptions are:\n- 'add' - add a sub-issue to a parent issue in a GitHub repository.\n- 'remove' - remove a sub-issue from a parent issue in a GitHub repository.\n- 'reprioritize' - change the order of sub-issues within a parent issue in a GitHub repository. Use either 'after_id' or 'before_id' to specify the new position.\nWrites issue hierarchy. To move a sub-issue to a new parent, use `add` with `replace_parent=true`; there is no writable parent field.\n",
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        replace_parent: {
          description:
            "When true, replaces the sub-issue's current parent issue. Use with 'add' method only.",
          type: "boolean",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        sub_issue_id: {
          description:
            "The ID of the sub-issue to add. ID is not the same as issue number",
          type: "number",
        },
      },
      required: ["method", "owner", "repo", "issue_number", "sub_issue_id"],
      type: "object",
    },
    name: "sub_issue_write",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Update issue comment",
    },
    description:
      "Update the body of an existing issue or pull request conversation comment. This tool cannot update pull request review comments.",
    inputSchema: {
      properties: {
        body: {
          description: "New comment content",
          minLength: 1,
          type: "string",
        },
        comment_id: {
          description:
            "The numeric ID of the issue or pull request conversation comment to update. Do not use a pull request review comment ID.",
          minimum: 1,
          type: "integer",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "comment_id", "body"],
      type: "object",
    },
    name: "update_issue_comment",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Edit pull request",
    },
    description: "Update an existing pull request in a GitHub repository.",
    inputSchema: {
      properties: {
        base: {
          description: "New base branch name",
          type: "string",
        },
        body: {
          description: "New description",
          type: "string",
        },
        draft: {
          description:
            "Mark pull request as draft (true) or ready for review (false)",
          type: "boolean",
        },
        maintainer_can_modify: {
          description: "Allow maintainer edits",
          type: "boolean",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        pullNumber: {
          description: "Pull request number to update",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
        reviewers: {
          description:
            "GitHub usernames or ORG/team-slug team reviewers to request reviews from",
          items: {
            type: "string",
          },
          type: "array",
        },
        state: {
          description: "New state",
          enum: ["open", "closed"],
          type: "string",
        },
        title: {
          description: "New title",
          type: "string",
        },
      },
      required: ["owner", "repo", "pullNumber"],
      type: "object",
    },
    name: "update_pull_request",
  },
  {
    annotations: {
      idempotentHint: false,
      readOnlyHint: false,
      title: "Update pull request branch",
    },
    description:
      "Update the branch of a pull request with the latest changes from the base branch.",
    inputSchema: {
      properties: {
        expectedHeadSha: {
          description: "The expected SHA of the pull request's HEAD ref",
          type: "string",
        },
        owner: {
          description: "Repository owner",
          type: "string",
          "x-mcp-header": "owner",
        },
        pullNumber: {
          description: "Pull request number",
          type: "number",
        },
        repo: {
          description: "Repository name",
          type: "string",
          "x-mcp-header": "repo",
        },
      },
      required: ["owner", "repo", "pullNumber"],
      type: "object",
    },
    name: "update_pull_request_branch",
  },
];
